<template>
  <div>
    <VModal 
      v-if="show" 
      v-model="modalOpen" 
      @update:modelValue="closeModal" 
      :click-to-close="true" 
      :esc-to-close="true" 
      size="full" 
      :id="'mobile-filter-modal'" 
      :z-index="9999"
    >
      <!-- Modal başlık -->
      <template #header-content>
        <h2 class="text-base sm:text-lg font-bold text-gray-800 whitespace-normal w-full">Filtreleme Seçenekleri</h2>
      </template>

      <div class="h-full overflow-y-auto p-4 pb-24 space-y-4">
        <!-- Arama - (Moved to top on mobile) -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Arama</label>
          <VInput
            v-model="localSearchQuery"
            type="text"
            placeholder="Dosya adı veya açıklama ara..."
            :showIcon="true"
            icon="search"
            iconClass="text-gray-400 !text-base"
            size="sm"
            :showClearButton="!!localSearchQuery"
            :showError="false"
            :maxLength="50"
          />
        </div>

        <!-- Dosya Türü Filtresi -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Dosya Türü</label>
          <CustomSelect v-model="localFilters.fileType" :options="fileTypeOptions" placeholder="Tümü" class="w-full" :clearable="true"/>
        </div>

        <!-- Kategori Filtresi -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Kategoriler</label>
          <CategoryPicker v-model="localFilters.categoryIds" :categories="formattedCategoryOptions" placeholder="Kategorileri seçin..."/>
        </div>

        <!-- Seçili Kategoriler - Burayı kaldırıyoruz çünkü seçimler aktif filtrelerde görünecek -->

        <!-- Tarih Filtresi -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Tarih</label>
          <CustomSelect v-model="localFilters.dateFilter" :options="dateFilterOptions" placeholder="Tümü" class="w-full" :clearable="true"/>
        </div>

        <!-- Aktif Filtreler -->
        <div v-if="hasLocalFilters" class="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
          <div class="text-sm font-medium text-gray-700 mb-3">Aktif Filtreler</div>
          <div class="flex flex-wrap gap-2 w-full">
            <div v-for="(filter, key) in localActiveFilters" :key="key" 
                 class="filter-tag flex items-start bg-primary/10 text-primary text-sm rounded-lg px-3 py-2 border border-primary/10 shadow-sm w-full max-w-full"
                 :class="{ 'bg-primary/5 border-primary/5': filter.isCounter }">
              <div class="flex flex-col flex-1 min-w-0 overflow-hidden">
                <span class="font-medium mr-1">{{ filter.label }}:</span>
                <span :class="{ 'font-medium': filter.isCounter, 'break-words': key === 'search', 'line-clamp-2': key === 'search' }" class="overflow-hidden text-ellipsis">{{ filter.value }}</span>
              </div>
              <button @click="removeLocalFilter(key)" class="ml-2 flex-shrink-0 h-6 w-6 flex items-center justify-center text-primary hover:text-red-500 hover:bg-red-50 rounded-full focus:outline-none transition-colors">
                <span class="material-symbols-outlined !text-[18px]">close</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Aksiyon Butonları -->
        <div class="sticky bottom-0 left-0 right-0 bg-white border-t pt-4 pb-4 px-4 flex space-x-3 mt-6">
          <CustomButton button-text="Sıfırla" @click="resetLocalFilters" variant="secondary" size="md" :full-width="true"/>
          <CustomButton button-text="Filtreleri Uygula" @click="applyLocalFilters" :is-loading="isLoading" size="md" variant="primary" :full-width="true"/>
        </div>
      </div>
    </VModal>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch, reactive, onMounted } from 'vue'
import VModal from '@/components/modals/VModal.vue'
import CustomButton from '@/components/base/buttons/VButton.vue'
import CustomSelect from '@/components/base/inputs/VSelect.vue'
import VInput from '@/components/base/inputs/VInput.vue'
import CategoryPicker from './CategoryPicker.vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  filters: {
    type: Object,
    required: true,
  },
  searchQuery: {
    type: String,
    default: '',
  },
  categoryOptions: {
    type: Array,
    required: true,
  },
  fileTypeOptions: {
    type: Array,
    required: true,
  },
  dateFilterOptions: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'update:show',
  'apply-filters',
  'reset-filters',
  'update:search-query',
])

const modalOpen = ref(props.show)
const localSearchQuery = ref(props.searchQuery)

// Yerel filtreler - props'ları doğrudan değiştirmemek için
const localFilters = reactive({
  fileType: props.filters.fileType,
  categoryIds: [...(props.filters.categoryIds || [])],
  dateFilter: props.filters.dateFilter,
})

// Yerel kategori kaldırma
const removeLocalCategory = (categoryId) => {
  if (!localFilters.categoryIds) return

  localFilters.categoryIds = localFilters.categoryIds.filter(id =>
      id !== categoryId &&
      id !== Number(categoryId) &&
      id !== String(categoryId),
  )
}

// Kategori adını ID'ye göre bulma
const getCategoryName = (categoryId) => {
  const category = props.categoryOptions.find(c =>
      c.value === categoryId ||
      c.value === Number(categoryId) ||
      c.value === String(categoryId),
  )
  return category ? category.label : 'Bilinmeyen Kategori'
}

// Kategorileri formatlama
const formattedCategoryOptions = computed(() => {
  // Çocukları olan bir ağaç yapısı oluştur
  return buildCategoryTree(props.categoryOptions)
})

// Kategori ağacı oluşturan yardımcı fonksiyon
const buildCategoryTree = (categories) => {
  // Önce ID'ye göre tüm kategorileri haritala
  const categoryMap = {}
  categories.forEach(cat => {
    categoryMap[cat.value] = {
      id: cat.value,
      label: cat.label,
      parentId: cat.parentId || null,
      children: [],
    }
  })

  // Şimdi ağacı oluştur
  const rootCategories = []
  categories.forEach(cat => {
    if (cat.parentId && categoryMap[cat.parentId]) {
      // Eğer parent'i varsa, parent'in child'larına ekle
      categoryMap[cat.parentId].children.push(categoryMap[cat.value])
    } else {
      // Parent'i yoksa veya geçersizse, kök kategorilere ekle
      rootCategories.push(categoryMap[cat.value])
    }
  })

  return rootCategories
}

  // Aktif filtreler
const localActiveFilters = computed(() => {
  const result = {}

  if (localFilters.fileType) {
    const option = props.fileTypeOptions.find(opt => opt.value === localFilters.fileType)
    result.fileType = {
      label: 'Dosya Türü',
      value: option ? option.label : localFilters.fileType,
    }
  }

  if (localFilters.categoryIds && localFilters.categoryIds.length > 0) {
    // Seçilen tüm kategorilerin adlarını bulup birleştir
    const selectedCategories = localFilters.categoryIds.map(catId => {
      const option = props.categoryOptions.find(opt =>
          opt.value === catId ||
          opt.value === Number(catId) ||
          opt.value === String(catId),
      )
      return option ? option.label : `Kategori ${catId}`
    })

    // Her bir kategoriyi ayrı bir filtre olarak ekle, maksimum 3 tane göster
    if (selectedCategories.length <= 3) {
      selectedCategories.forEach((categoryName, index) => {
        result[`category_${index}`] = {
          label: 'Kategori',
          value: categoryName,
          originalKey: 'categoryIds'
        }
      })
    } else {
      // 3'ten fazla kategori seçilmişse, ilk 2'sini gösterip + diğerleri şeklinde göster
      selectedCategories.slice(0, 2).forEach((categoryName, index) => {
        result[`category_${index}`] = {
          label: 'Kategori',
          value: categoryName,
          originalKey: 'categoryIds'
        }
      })
      
      result.categoryCount = {
        label: 'Kategori',
        value: `+${selectedCategories.length - 2} diğer`,
        originalKey: 'categoryIds',
        isCounter: true
      }
    }
  }

  if (localFilters.dateFilter) {
    const option = props.dateFilterOptions.find(opt => opt.value === localFilters.dateFilter)
    result.dateFilter = {
      label: 'Tarih',
      value: option ? option.label : localFilters.dateFilter,
    }
  }

  if (localSearchQuery.value) {
    result.search = {
      label: 'Arama',
      value: localSearchQuery.value,
    }
  }

  return result
})

const hasLocalFilters = computed(() => Object.keys(localActiveFilters.value).length > 0)

// Modalı kapat
const closeModal = () => {
  modalOpen.value = false
  emit('update:show', false)
}

// Filtreleri uygula
const applyLocalFilters = () => {
  // First update the search query
  emit('update:search-query', localSearchQuery.value)

  // Then apply all filters
  emit('apply-filters', {
    ...localFilters,
    searchQuery: localSearchQuery.value,
  })

  // Close modal after applying filters
  closeModal()
}

// Filtreleri sıfırla
const resetLocalFilters = () => {
  localFilters.fileType = null
  localFilters.categoryIds = []
  localFilters.dateFilter = null
  localSearchQuery.value = ''

  emit('reset-filters')
  emit('update:search-query', '')
  closeModal()
}

// Belirli bir filtreyi kaldır
const removeLocalFilter = (filterKey) => {
  if (filterKey === 'search') {
    localSearchQuery.value = ''
  } else if (filterKey.startsWith('category_') || filterKey === 'categoryCount') {
    // Tüm kategorileri temizle
    localFilters.categoryIds = []
  } else if (filterKey === 'categoryIds') {
    // Geriye uyumluluk için
    localFilters.categoryIds = []
  } else {
    // Standart filtreler
    localFilters[filterKey] = null
  }
}

// Make sure modal values sync with props
const syncModalValues = () => {
  localFilters.fileType = props.filters.fileType
  localFilters.categoryIds = [...(props.filters.categoryIds || [])]
  localFilters.dateFilter = props.filters.dateFilter
  localSearchQuery.value = props.searchQuery
}

// Props değişimini izle
watch(() => props.show, (newVal) => {
  modalOpen.value = newVal
  if (newVal) {
    // Modal açıldığında güncel değerleri al
    syncModalValues()
  }
}, { immediate: true })

// Modal durumunu izle
watch(modalOpen, (newVal) => {
  if (!newVal) {
    emit('update:show', false)
  }
})

// If filters change externally, update local values
watch(() => props.filters, () => {
  if (modalOpen.value) {
    syncModalValues()
  }
}, { deep: true })

// If search query changes externally, update local value
watch(() => props.searchQuery, (newVal) => {
  if (modalOpen.value) {
    localSearchQuery.value = newVal
  }
})

// Ensure the modal values are synced on mount
onMounted(() => {
  syncModalValues()
})
</script>

<style scoped>
/* Touch-friendly style for mobile elements */
.rounded-full {
  @apply overflow-hidden;
}

button {
  @apply min-h-[32px] min-w-[32px];
}

/* Animation for modal transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Filter tag styles */
.filter-tag {
  max-width: 100%;
  min-height: 44px;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
}

.filter-tag span.break-words {
  word-break: break-word;
  word-wrap: break-word;
  white-space: normal;
  line-height: 1.4;
  max-width: calc(100% - 40px);
}
</style>